'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiFetch, getAccessToken, clearAccessToken, getTokenExpiry, tryRefreshToken } from '@/lib/apiClient';

interface Branding { logoUrl: string | null; displayName: string | null; primaryColor: string }

// ─── Nav items ──────────────────────────────────────────────────────────────

const NAV = [
  {
    label: 'Zgłoszenia',
    items: [
      { href: '/panel/tickets', label: 'Lista zgłoszeń', icon: ClipboardListIcon },
      { href: '/panel/map',     label: 'Mapa',           icon: MapIcon },
      { href: '/panel/history', label: 'Historia',       icon: ArchiveIcon },
    ],
  },
  {
    label: 'Sprawy',
    items: [
      { href: '/panel/clusters', label: 'Sprawy zbiorcze', icon: LayersIcon },
    ],
  },
  {
    label: 'Administracja',
    items: [
      { href: '/panel/settings/users', label: 'Użytkownicy', icon: UsersIcon },
      { href: '/panel/email',    label: 'E-mail',       icon: MailIcon },
      { href: '/panel/pdf',      label: 'Szablony PDF', icon: PdfIcon },
      { href: '/panel/settings', label: 'Ustawienia',   icon: SettingsIcon },
    ],
  },
];

// ─── Icons (inline SVG) ──────────────────────────────────────────────────────

function ClipboardListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}
function MapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );
}
function ArchiveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  );
}
function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}
function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function PdfIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6" />
    </svg>
  );
}
function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}
function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// ─── SessionWatcher — proaktywne odświeżanie sesji + modal ostrzegawczy ───────

const WARN_BEFORE_MS = 5 * 60 * 1000; // pokaż modal 5 min przed wygaśnięciem

function SessionWatcher() {
  // null = brak modalu; liczba = sekundy pozostałe do wylogowania
  const [secsLeft, setSecsLeft] = useState<number | null>(null);

  const checkAndRefresh = useCallback(async () => {
    const expiry = getTokenExpiry();
    if (!expiry) return;
    const msLeft = expiry.getTime() - Date.now();
    if (msLeft > WARN_BEFORE_MS) return; // jeszcze dużo czasu — nic nie rób

    // Spróbuj cicho odświeżyć
    const ok = await tryRefreshToken();
    if (ok) {
      setSecsLeft(null); // udało się — zamknij modal jeśli był otwarty
    } else {
      // Nie udało się — pokaż modal z czasem do wylogowania
      setSecsLeft(s => s ?? Math.max(0, Math.floor(msLeft / 1000)));
    }
  }, []);

  // Sprawdzaj co minutę
  useEffect(() => {
    const id = setInterval(checkAndRefresh, 60_000);
    // Pierwsze sprawdzenie po 5 s od zamontowania
    const t = setTimeout(checkAndRefresh, 5_000);
    return () => { clearInterval(id); clearTimeout(t); };
  }, [checkAndRefresh]);

  // Odliczanie — co sekundę zmniejszaj licznik
  useEffect(() => {
    if (secsLeft === null) return;
    if (secsLeft <= 0) {
      clearAccessToken();
      window.location.href = '/panel/login';
      return;
    }
    const t = setTimeout(() => setSecsLeft(s => (s !== null ? s - 1 : null)), 1_000);
    return () => clearTimeout(t);
  }, [secsLeft]);

  const handleRenew = async () => {
    const ok = await tryRefreshToken();
    if (ok) {
      setSecsLeft(null);
    } else {
      clearAccessToken();
      window.location.href = '/panel/login';
    }
  };

  const handleLogout = () => {
    clearAccessToken();
    window.location.href = '/panel/login';
  };

  if (secsLeft === null) return null;

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      {/* Karta modalu */}
      <div className="relative bg-white rounded-2xl shadow-2xl border border-amber-200 max-w-sm w-full mx-4 overflow-hidden">

        {/* Pasek gradientu na górze */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400" />

        {/* Nagłówek */}
        <div className="px-6 pt-6 pb-4 flex items-start gap-4">
          <div className="shrink-0 w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
            <ClockIcon className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Sesja wygasa wkrótce</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Zostaniesz automatycznie wylogowany za
            </p>
          </div>
        </div>

        {/* Odliczanie */}
        <div className="px-6 py-4 flex items-center justify-center">
          <span className="text-5xl font-bold tabular-nums tracking-tight text-amber-600">
            {mins}:{String(secs).padStart(2, '0')}
          </span>
        </div>

        {/* Wskazówka */}
        <p className="px-6 pb-4 text-xs text-center text-gray-400">
          Kliknij „Przedłuż sesję", aby kontynuować pracę bez ponownego logowania.
        </p>

        {/* Przyciski */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={handleRenew}
            className="flex-1 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-medium transition-colors shadow-sm"
          >
            Przedłuż sesję
          </button>
          <button
            onClick={handleLogout}
            className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 hover:bg-gray-50 active:bg-gray-100 text-gray-600 text-sm font-medium transition-colors"
          >
            Wyloguj
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NewsBadge (polling /api/tickets/count) ──────────────────────────────────

function NewsBadge() {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet<{ count: number }>('/tickets/count?status=NOWE');
      setCount(data.count ?? 0);
    } catch {
      // silent — nie blokuj UI
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!count) return null;

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-400 text-white animate-pulse">
      {count}
    </span>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ collapsed, onToggle, branding }: {
  collapsed: boolean; onToggle: () => void; branding: Branding | null
}) {
  const pathname = usePathname();

  const isActive = (href: string) =>
    href === '/panel' ? pathname === '/panel' : pathname.startsWith(href);

  return (
    <aside
      className={`
        relative flex flex-col border-r border-gray-200 bg-white
        transition-all duration-200 ease-in-out shrink-0
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Logo tenanta */}
      <div className={`flex items-center h-16 px-3 border-b border-gray-200 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        {branding?.logoUrl ? (
          <div className={`flex items-center ${collapsed ? '' : 'gap-3'} overflow-hidden`}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={branding.logoUrl}
              alt={branding.displayName ?? 'Logo gminy'}
              className={`${collapsed ? 'h-9 w-9' : 'h-9 w-auto max-w-[120px]'} object-contain shrink-0`}
            />
            {!collapsed && branding.displayName && (
              <span className="text-sm font-semibold text-gray-900 truncate">{branding.displayName}</span>
            )}
          </div>
        ) : (
          <span className={`text-sm font-semibold text-gray-900 truncate ${collapsed ? 'hidden' : ''}`}>
            {branding?.displayName ?? 'Panel Urzędnika'}
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {NAV.map((group) => (
          <div key={group.label}>
            {!collapsed && (
              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`
                        flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium
                        transition-colors duration-150
                        ${active
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                    >
                      <item.icon className={`w-5 h-5 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
                      {!collapsed && (
                        <>
                          <span className="flex-1">{item.label}</span>
                          {item.href === '/panel/tickets' && <NewsBadge />}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Logo aplikacji — wordmark „HaloGmina" (ukryty w wąskim, zwiniętym pasku) */}
      {!collapsed && (
        <div className="border-t border-gray-100 px-3 py-3 flex items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/brand/logo/halogmina-horizontal.svg"
            alt="HaloGmina.pl"
            className="h-7 w-auto max-w-[140px] object-contain opacity-80"
          />
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full border border-gray-200 bg-white shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors"
        title={collapsed ? 'Rozwiń sidebar' : 'Zwiń sidebar'}
      >
        <ChevronLeftIcon className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
      </button>
    </aside>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────

interface Me { sub: string; email: string; role: string }

function Topbar({ me }: { me: Me | null }) {
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) router.push(`/panel/tickets?search=${encodeURIComponent(search.trim())}`);
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch { /* silent */ }
    clearAccessToken();
    window.location.href = '/panel/login';
  };

  const initials = me
    ? (me.email.slice(0, 2).toUpperCase())
    : '??';

  return (
    <header className="sticky top-0 z-30 h-16 flex items-center gap-4 px-6 bg-white border-b border-gray-200">
      {/* Wyszukiwarka */}
      <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          type="search"
          placeholder="Szukaj zgłoszenia… (Ctrl+K)"
          className="w-full h-9 pl-9 pr-4 rounded-full bg-gray-100 border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition"
        />
      </form>

      <div className="flex items-center gap-3 ml-auto">
        {/* Powiadomienia / licznik nowych */}
        <Link
          href="/panel/tickets?status=NOWE"
          className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
        >
          <BellIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Nowe</span>
          <NewsBadge />
        </Link>

        {/* Avatar + dropdown */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm font-semibold hover:bg-indigo-200 transition-colors"
            title={me?.email}
          >
            {initials}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-10 z-20 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 text-sm">
                <div className="px-4 py-2.5 border-b border-gray-100">
                  <p className="font-medium text-gray-800 truncate">{me?.email ?? '—'}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{me?.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                >
                  Wyloguj się
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── AppShell ─────────────────────────────────────────────────────────────────

export default function PanelLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  // Strony publiczne panelu (logowanie, reset hasła) — pomiń auth check i chrome
  const isLoginPage =
    pathname === '/panel/login' ||
    pathname === '/panel/reset-hasla' ||
    pathname === '/panel/nowe-haslo';

  const [collapsed, setCollapsed] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  // Na stronie logowania od razu authChecked=true, żeby nie pokazywać spinnera
  const [authChecked, setAuthChecked] = useState(isLoginPage);

  // Auth check — sprawdź token i pobierz dane użytkownika
  // (isLoginPage inicjalizuje authChecked=true od razu, bez spinnera)
  useEffect(() => {
    if (isLoginPage) {
      // authChecked już jest true (patrz useState poniżej), nic do roboty
      return;
    }
    const token = getAccessToken();
    if (!token) {
      // Spróbuj odświeżyć przez refresh-token cookie
      apiFetch('/auth/refresh', { method: 'POST' })
        .then(async (res) => {
          if (res.ok) {
            const { setAccessToken } = await import('@/lib/apiClient');
            const data: { accessToken: string } = await res.json();
            setAccessToken(data.accessToken);
            return apiGet<Me>('/auth/me');
          }
          throw new Error('no session');
        })
        .then((user) => { setMe(user); setAuthChecked(true); })
        .catch(() => { router.replace('/panel/login'); });
      return;
    }
    apiGet<Me>('/auth/me')
      .then((user) => { setMe(user); setAuthChecked(true); })
      .catch(() => { router.replace('/panel/login'); });
  }, [router, isLoginPage]);

  // Pobierz branding po zalogowaniu
  useEffect(() => {
    if (!authChecked || isLoginPage) return;
    apiGet<Branding>('/public/branding').then(setBranding).catch(() => {});
  }, [authChecked, isLoginPage]);

  // Ctrl+K → focus wyszukiwarki
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const input = document.querySelector<HTMLInputElement>('input[type="search"]');
        input?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Czekaj na weryfikację auth (nie na stronie logowania)
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Weryfikacja sesji…</p>
        </div>
      </div>
    );
  }

  // Strona logowania — renderuj bez sidebara i topbara
  if (isLoginPage) {
    return <>{children}</>;
  }

  return (
    <>
      <SessionWatcher />
      <div className="flex h-screen overflow-hidden bg-gray-50">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} branding={branding} />

        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          <Topbar me={me} />
          <main className="flex-1 overflow-y-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </>
  );
}
